import { __awaiter } from '../_virtual/_tslib.js';
import BloctoSDK from '@blocto/sdk';
import { ethers } from 'ethers';
import { logger } from '@dynamic-labs/wallet-connector-core';
import { EthWalletConnector } from '@dynamic-labs/ethereum';

class BloctoEvm extends EthWalletConnector {
    constructor(props) {
        var _a, _b;
        super(props);
        this.LOCAL_STORAGE_KEY = 'dynamic_blocto_evm_chain_id';
        this.SESSION_STORAGE_BLOCTO_KEY = 'BLOCTO_SDK';
        this.name = 'bloctoevm';
        this.canConnectViaCustodialService = true;
        this.connectedChain = 'EVM';
        this.supportedChains = ['EVM', 'ETH'];
        this.supportsNetworkSwitching = () => false;
        // Taken from : https://docs.blocto.app/blocto-sdk/javascript-sdk/evm-sdk/provider
        const BLOCTO_SUPPORTED_CHAIN_IDS = [1, 4, 56, 97, 137, 80001, 43114, 43113];
        this.evmNetworks = this.evmNetworks.filter((n) => BLOCTO_SUPPORTED_CHAIN_IDS.includes(n.chainId));
        this.chainId =
            Number(localStorage.getItem(this.LOCAL_STORAGE_KEY)) ||
                ((_a = this.evmNetworks.find((n) => n.chainId === 137)) === null || _a === void 0 ? void 0 : _a.chainId) ||
                ((_b = this.evmNetworks[0]) === null || _b === void 0 ? void 0 : _b.chainId);
        if (this.evmNetworks.length > 0) {
            this.initClient();
        }
    }
    // Check if there are already any accounts in the session
    // Blocto in version > 0.5.0 started to store the accounts in the session storage
    checkForSessionAccounts() {
        var _a, _b;
        try {
            const sessionValue = sessionStorage.getItem(this.SESSION_STORAGE_BLOCTO_KEY);
            const parsedSessionValue = JSON.parse(sessionValue);
            return (((_a = parsedSessionValue === null || parsedSessionValue === void 0 ? void 0 : parsedSessionValue.data) === null || _a === void 0 ? void 0 : _a.accounts) &&
                Object.keys((_b = parsedSessionValue === null || parsedSessionValue === void 0 ? void 0 : parsedSessionValue.data) === null || _b === void 0 ? void 0 : _b.accounts).length > 0);
        }
        catch (_c) {
            return false;
        }
    }
    initClient() {
        if (!this.client) {
            const network = this.evmNetworks.find((n) => n.chainId === this.chainId);
            if (network) {
                this.client = new BloctoSDK({
                    ethereum: {
                        chainId: `0x${this.chainId.toString(16)}`,
                        rpc: network.rpcUrls[0],
                    },
                });
            }
            else {
                logger.error("Couldn't find a configured network for Blocto EVM");
            }
        }
    }
    getClient() {
        return this.client;
    }
    removeClient() {
        this.client = undefined;
    }
    getWeb3Provider() {
        const client = this.getClient();
        // If there are no accounts yet, we shouldn't wrap the provider in ethers
        if ((client === null || client === void 0 ? void 0 : client.ethereum) && this.checkForSessionAccounts()) {
            return new ethers.providers.Web3Provider(client.ethereum);
        }
        else {
            return undefined;
        }
    }
    // We are calling the client directly and not via Web3Provider because if we will wrap it in Ethers,
    // Ethers will be making retries on failure. This becomes an issue on the connect step.
    // When Blocto's modal closes prior to receiving a response with the address on the connect step,
    // then ethers would retry and the modal will open again.
    fetchPublicAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const client = this.getClient();
                if (!(client === null || client === void 0 ? void 0 : client.ethereum)) {
                    return undefined;
                }
                const addresses = yield client.ethereum.request({
                    method: 'eth_requestAccounts',
                });
                if ((addresses === null || addresses === void 0 ? void 0 : addresses.length) > 0) {
                    return addresses[0];
                }
                return undefined;
            }
            catch (e) {
                logger.error(e);
                return Promise.reject(e);
            }
        });
    }
    signMessage(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const signer = yield this.getSigner();
                return yield (signer === null || signer === void 0 ? void 0 : signer.signMessage(messageToSign));
            }
            catch (e) {
                if (e.message === 'User declined the signing request') {
                    return Promise.reject({ code: '4001' });
                }
                else {
                    return Promise.reject(e);
                }
            }
        });
    }
    endSession() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            localStorage.removeItem('sdk.session');
            if (((_a = this.client) === null || _a === void 0 ? void 0 : _a.ethereum) && this.checkForSessionAccounts()) {
                yield this.client.ethereum.request({ method: 'wallet_disconnect' });
                this.removeClient();
            }
        });
    }
}

export { BloctoEvm };
